import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { GoldButton } from "./Ui";
import PayPalButton from "./PayPalButton";
import { CheckCircle2, CalendarClock } from "lucide-react";

const PLATFORMS = ["YouTube", "TikTok", "Instagram", "Facebook", "Other"];
const inputCls =
  "w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2.5 text-sm text-white outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]";
const chip = (active) =>
  `px-3 py-2 rounded-lg text-sm border transition-colors ${active ? "bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37] font-semibold" : "bg-black/40 text-white/80 border-white/10 hover:border-[#D4AF37]/60"}`;

export default function ConsultationBooking({ price }) {
  const [f, setF] = useState({ name: "", email: "", whatsapp: "", platform: "", goals: "" });
  const [slots, setSlots] = useState([]);
  const [slotsLoaded, setSlotsLoaded] = useState(false);
  const [selDate, setSelDate] = useState("");
  const [selKey, setSelKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/consultation/slots")
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoaded(true));
  }, []);

  const dates = [...new Map(slots.map((s) => [s.date, s.date_label])).entries()];
  const daySlots = slots.filter((s) => s.date === selDate);
  const selSlot = slots.find((s) => s.key === selKey);
  const hasSlots = slots.length > 0;

  const booking = () => ({
    WhatsApp: f.whatsapp || "",
    Platform: f.platform || "",
    Goals: f.goals || "",
    slot_key: selKey || "",
    "Session time": selSlot ? `${selSlot.date_label} · ${selSlot.time_label} (UK time)` : "",
  });

  const validate = () => {
    if (!f.name || !f.email) { toast.error("Please enter your name and email."); return false; }
    if (hasSlots && !selKey) { toast.error("Please pick a date and time for your session."); return false; }
    return true;
  };

  const payload = useCallback(
    () =>
      f.name && f.email && (!hasSlots || selKey)
        ? { kind: "consultation", customer_name: f.name, customer_email: f.email, booking: booking() }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [f, selKey, hasSlots]
  );

  const startStripe = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { ...payload(), origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail) || "Could not start checkout.");
      setLoading(false);
    }
  };

  const onPaypalSuccess = () => { setDone(true); toast.success("Payment successful!"); };

  if (done)
    return (
      <div data-testid="consultation-paid" className="card-surface rounded-2xl p-8 text-center gold-glow">
        <CheckCircle2 className="mx-auto text-[#D4AF37]" size={44} />
        <h3 className="mt-4 text-xl font-display">Payment received!</h3>
        <p className="mt-2 text-white/70 text-sm">
          Your 1:1 consultation is booked{selSlot ? ` for ${selSlot.date_label} at ${selSlot.time_label} (UK time)` : ""}. We've emailed
          your confirmation, and you'll get your FREE 7-day 'Clickable Video Ideas Plan' after the session.
        </p>
      </div>
    );

  return (
    <form onSubmit={startStripe} data-testid="consultation-form" className="card-surface rounded-2xl p-6 sm:p-8 space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Full name *</label>
          <input required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="consultation-name" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Email *</label>
          <input required type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="consultation-email" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">WhatsApp number</label>
          <input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} data-testid="consultation-whatsapp" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Preferred platform</label>
          <select value={f.platform} onChange={(e) => set("platform", e.target.value)} data-testid="consultation-platform" className={inputCls}>
            <option value="">Select...</option>
            {PLATFORMS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="block text-xs uppercase tracking-wider text-white/50 mb-1.5">Your goals</label>
          <textarea rows={3} placeholder="What do you want to achieve?" value={f.goals} onChange={(e) => set("goals", e.target.value)} data-testid="consultation-goals" className={inputCls} />
        </div>
      </div>

      {slotsLoaded && hasSlots && (
        <div data-testid="consultation-slots" className="rounded-xl border border-white/10 bg-black/20 p-4">
          <div className="flex items-center gap-2 text-[#D4AF37] mb-3"><CalendarClock size={16} /><span className="text-sm font-medium">Pick your session time <span className="text-white/40">(UK time)</span></span></div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="consultation-dates">
            {dates.map(([d, label]) => (
              <button type="button" key={d} onClick={() => { setSelDate(d); setSelKey(""); }} data-testid={`slot-date-${d}`} className={chip(selDate === d)}>{label}</button>
            ))}
          </div>
          {selDate && (
            <div className="flex flex-wrap gap-2" data-testid="consultation-times">
              {daySlots.map((s) => (
                <button type="button" key={s.key} onClick={() => setSelKey(s.key)} data-testid={`slot-time-${s.key}`} className={chip(selKey === s.key)}>{s.time_label}</button>
              ))}
            </div>
          )}
          {selSlot && <p className="mt-3 text-xs text-[#D4AF37]">Selected: {selSlot.date_label} at {selSlot.time_label} (UK time)</p>}
        </div>
      )}
      {slotsLoaded && !hasSlots && (
        <p className="text-xs text-white/50">No set times available right now — book below and we'll email you to arrange a time.</p>
      )}

      <div>
        <GoldButton type="submit" disabled={loading} data-testid="consultation-pay-btn" className="w-full sm:w-auto">
          {loading ? "Starting checkout..." : `Pay £${price} & Book`}
        </GoldButton>
        <p className="mt-2 text-xs text-white/40">Secure card payment via Stripe. You'll get a confirmation email straight away.</p>
      </div>
      <PayPalButton getPayload={payload} onSuccess={onPaypalSuccess} />
    </form>
  );
}
